import DSBox from '../../../design-system/DSBox';
import DSGrid from '../../../design-system/DSGrid';
import CollectionInfo from '../../Collections/components/CollectionPreview/CollectionInfo';
import PublicKlydoCardView from './PublicKlydoCardView';
import DSCircularProgress from '../../../design-system/DSCircularProgress';
import { useNavigate, useParams } from 'react-router-dom';
import usePublicCollectionQuery from '../hooks/usePublicCollectionQuery';
import { useTheme } from '@mui/material';
import { useCallback, useState } from 'react';
import { KlydoPreview } from '../../../api/public';
import { hasSound } from '../../../utils';
import DSTabGradientViewPaper from '../../../design-system/Paper/DSTabGradientViewPaper';
import DSAvatar from '../../../design-system/DSAvatar';
import { DSTypography } from '../../../design-system/Typography';
import DSTabButton from '../../../design-system/DSTabButton';
import DSDivider from '../../../design-system/DSDivider';
import AssetBox, {
  CoverImageAssetBox,
} from '../../Collections/components/CollectionPreview/Tabs/AssetBox';
import {
  PublicCollectionTab,
  publicCollectionTabLabels,
  publicCollectionTabs,
} from '../types/publicCollectionTabs';

const PublicCollectionView = () => {
  const { collectionId } = useParams<{ collectionId: string }>();
  const {
    data: collection,
    isLoading,
    error,
  } = usePublicCollectionQuery(collectionId!);
  const { spacing } = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<PublicCollectionTab>(
    PublicCollectionTab.Content,
  );

  const handleKlydoCardClick = useCallback(
    (klydo: KlydoPreview) => {
      navigate(`/public/collection/${collectionId}/klydo/${klydo.id}`);
    },
    [collectionId, navigate],
  );

  if (isLoading) {
    return (
      <DSBox
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: '#1E1E1E',
          color: '#FFFFFF',
        }}
      >
        <DSCircularProgress />
      </DSBox>
    );
  }

  if (error || !collection) {
    return (
      <DSBox
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: '#1E1E1E',
          color: '#FFFFFF',
          textAlign: 'center',
        }}
      >
        <DSTypography variant="h4">
          {error?.response?.data?.message ||
            error?.message ||
            'Collection not found'}
        </DSTypography>
      </DSBox>
    );
  }

  return (
    <DSTabGradientViewPaper>
      <DSBox
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          margin: spacing(3),
          paddingY: spacing(4),
          gap: spacing(3),
        }}
      >
        <DSBox
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <DSTypography
            variant="h4"
            sx={{
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              whiteSpace: 'normal',
              WebkitLineClamp: 2,
              wordWrap: 'break-word',
              width: '100%',
              textAlign: 'center',
            }}
          >
            {collection.name}
          </DSTypography>
        </DSBox>

        {collection.creator && (
          <DSBox
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignContent: 'center',
              alignItems: 'center',
              gap: spacing(1),
            }}
          >
            <DSAvatar
              src={collection.creator?.photoUrl}
              alt={collection.creator?.name}
            />
            <DSTypography variant="h3">{collection.creator?.name}</DSTypography>
          </DSBox>
        )}

        <DSBox>
          <CollectionInfo
            description={collection?.description}
            cover={collection?.coverUrl}
            klydoCount={collection?.klydos?.length || 0}
            hasChime={collection?.klydos.some(
              (klydo) => klydo.chimesUrls && klydo.chimesUrls.length > 0,
            )}
            hasSound={collection?.klydos.some((klydo) =>
              hasSound(klydo.capabilitiesV2?.animationSound?.mode),
            )}
            hasThirteenPlusTag={collection?.klydos.some((klydo) =>
              klydo.tags?.includes('13+'),
            )}
            hasPSFTag={collection?.klydos.some((klydo) =>
              klydo.tags?.includes('PSF'),
            )}
          />
        </DSBox>
      </DSBox>

      <DSBox
        sx={{
          display: 'flex',
          gap: spacing(1),
          flexDirection: 'row',
          overflowX: 'auto',
          overflowY: 'hidden',
          maxWidth: {
            xxs: '100%',
            xs: '50%',
            sm: '60%',
            md: '70%',
            lg: '80%',
          },
          paddingBottom: spacing(0.5),
          paddingX: spacing(3),
          marginTop: spacing(2),
          '&::-webkit-scrollbar': {
            height: '4px',
          },
        }}
      >
        {publicCollectionTabs.map((tab) => (
          <DSTabButton
            key={tab}
            title={publicCollectionTabLabels[tab]}
            isSelected={activeTab === tab}
            onClick={() => setActiveTab(tab)}
          />
        ))}
      </DSBox>
      <DSDivider sx={{ marginTop: spacing(1), marginBottom: spacing(4) }} />

      {activeTab === PublicCollectionTab.Content && (
        <DSBox>
          <DSGrid
            container
            spacing={spacing(3)}
            columns={{ xs: 1 }}
            sx={{
              justifyContent: 'center',
              paddingY: spacing(3),
            }}
          >
            {collection?.klydos?.map((klydo) => {
              return (
                <PublicKlydoCardView
                  key={klydo.id}
                  klydo={klydo}
                  pendulumLogo={collection.logoUrl}
                  onSelect={handleKlydoCardClick}
                />
              );
            })}
          </DSGrid>
        </DSBox>
      )}

      {activeTab === PublicCollectionTab.Assets && (
        <DSBox
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: spacing(4),
            paddingX: spacing(3),
            paddingBottom: spacing(3),
          }}
        >
          <CoverImageAssetBox
            label="Cover"
            imageUrl={collection.coverUrl}
            alt="Collection Cover"
          />
          <AssetBox
            label="Logo"
            imageUrl={collection.logoUrl}
            alt="Collection Logo"
          />
        </DSBox>
      )}
    </DSTabGradientViewPaper>
  );
};

export default PublicCollectionView;
